import {
  Center,
  useToast,
  VStack,
  Box,
  Flex,
  Avatar,
  Text,
  ButtonGroup,
  Button,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useIdentityContext } from "../../contexts/IdentityContext";
import { useAxios } from "../../hooks/useAxios";
import { useNavigate } from "react-router-dom";
import { useLayoutMode, LAYOUTMODES } from "../../hooks/useLayoutMode";
import { useRequestIsLoading } from "../../hooks/useRequestIsLoading";
import { useColorsContext } from "@buildsoft-eu/react";
import {
  DeleteProfileUrl,
  UndoDeleteProfileUrl,
  ProfileUrl,
} from "../../services/urlService";

export default function ResendProfileDeletinoCodePage({ minH }) {
  const { user } = useIdentityContext();
  const etag = user?.[`${process.env.REACT_APP_USER_METADATA}`].etag;
  const { putRequest, getRequest } = useAxios();
  const navigate = useNavigate();
  var { mode } = useLayoutMode();
  const { t } = useTranslation();
  const { requestIsLoading, setRequestIsLoading } = useRequestIsLoading();
  const {
    requestIsLoading: undoRequestIsLoading,
    setRequestIsLoading: setUndoRequestIsLoading,
  } = useRequestIsLoading();
  const { panel: panelColors } = useColorsContext();
  const toast = useToast();
  const gridStyleProps = {
    bg: panelColors.background,
    w: "100%",
    boxShadow: "lg",
    display: "flex",
    borderWidth: "1px",
    rounded: "md",
    p: "10",
    spacing: "3",
    borderTop: "4px",
    borderTopColor: "red.600",
    direction: {
      base: "column",
      md: "row",
    },
    justify: { base: "center" },
    align: { base: "center", md: "center" },
  };
  const showError = (errorMessage) => {
    toast({
      title: errorMessage,
      status: "error",
      isClosable: true,
    });
  };

  return (
    <Center
      minH={minH}
      display="flex"
      alignItems="baseline"
      p="5"
      justifyContent="center"
    >
      <Box minW={{ base: "92vw", lg: "90vw", xl: "80vw", "2xl": "75vw" }}>
        <VStack {...gridStyleProps} justifyContent="center">
          <Flex
            w="100%"
            gap="10"
            direction={{ base: "column", md: "row" }}
            mt="1"
            alignItems="center"
            justify="center"
          >
            <VStack>
              <Avatar
                size={mode === LAYOUTMODES.DESKTOP ? "2xl" : "xl"}
                ml="10"
                mr="10"
                src={user?.picture}
              />
              <Text mb="-3">{user?.name}</Text>
              <Text fontSize="sm">{user?.email}</Text>
            </VStack>
            <VStack alignItems="flex-start">
              <Text>{t("deleteProfile.resendDeleteCode")}</Text>
            </VStack>
          </Flex>
          <ButtonGroup
            pt="5"
            variant="outline"
            spacing="8"
            w="100%"
            justifyContent="center"
          >
            <Button
              variant="unstyled"
              isLoading={undoRequestIsLoading}
              loadingText={t("deleteProfile.undoDeleteAccount")}
              onClick={async () => {
                setUndoRequestIsLoading(true);
                const getNewEtag = async () => {
                  const { response, isError } = await getRequest(ProfileUrl);
                  return isError ? null : response.data?.eTag;
                };
                const successActions = () => {
                  setUndoRequestIsLoading(false);
                  navigate("/", { replace: true });
                };
                const displayError = () => {
                  showError(t("errors.undoDeleteProfile"));
                  setUndoRequestIsLoading(false);
                };
                const { response, isError } = await putRequest(
                  UndoDeleteProfileUrl,
                  {},
                  etag
                );
                if (!isError) {
                  successActions();
                  return;
                }
                if (response.response?.status !== 409) {
                  if (
                    response.response?.data[0]?.source?.reference ===
                    "/deleteRequested"
                  ) {
                    successActions();
                    return;
                  }
                  displayError();
                  return;
                }
                const newEtag = await getNewEtag();
                if (!newEtag) {
                  return;
                }
                const { response: responseAfterConflict, isError: isErrorConflict } = await putRequest(
                  UndoDeleteProfileUrl,
                  {},
                  newEtag
                );
                if (isErrorConflict) {
                  if (
                    responseAfterConflict.response?.data[0]?.source
                      ?.reference === "/deleteRequested"
                  ) {
                    successActions();
                    return;
                  }
                  displayError();
                  return;
                }
                successActions();
              }}
            >
              {t("deleteProfile.undoDeleteAccount")}
            </Button>
            <Button
              isLoading={requestIsLoading}
              loadingText={t("deleteProfile.sendNewCode")}
              _hover={{ bgColor: "red.100" }}
              spinnerPlacement="end"
              bgColor="red.100"
              textColor="red.600"
              onClick={async () => {
                setRequestIsLoading(true);
                const getNewEtag = async () => {
                  const { response, isError } = await getRequest(ProfileUrl);
                  return isError ? null : response.data?.eTag;
                };
                const successActions = () => {
                  setRequestIsLoading(false);
                  navigate("/");
                };
                const displayError = () => {
                  showError(t("errors.deleteProfile"));
                  setRequestIsLoading(false);
                };
                const { response, isError } = await putRequest(
                  DeleteProfileUrl,
                  {},
                  etag
                );

                if (!isError) {
                  successActions();
                  return;
                }
                if (response.response?.status !== 409) {
                  displayError();
                  return;
                }
                const newEtag = await getNewEtag();
                if (!newEtag) {
                  displayError();
                  return;
                }
                const { isError: isErrorConflict } = await putRequest(
                  DeleteProfileUrl,
                  {},
                  newEtag
                );
                if (isErrorConflict) {
                  displayError();
                  return;
                }
                successActions();
              }}
            >
              {t("deleteProfile.sendNewCode")}
            </Button>
          </ButtonGroup>
        </VStack>
      </Box>
    </Center>
  );
}
